import { AlertCircle, ArrowRight, HeartHandshake } from "lucide-react";
import { useGetList } from "ra-core";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import type { Contact, Task } from "../types";

const warmStrengths = new Set(["A", "B"]);
const strategicRoles = new Set(["1", "2"]);
const humanReviewMoves = new Set([
  "Referral Ask",
  "Intro Ask",
  "Email",
  "DM",
  "Podcast Invite",
]);

type Recommendation = {
  contact: Contact;
  score: number;
  action: string;
  reasons: string[];
  openTaskCount: number;
  daysSinceMeaningfulInteraction: number | null;
  humanReviewRequired: boolean;
};

export const RelationshipActionReview = () => {
  const { data: contacts, isPending: contactsPending } = useGetList<Contact>(
    "contacts",
    {
      pagination: { page: 1, perPage: 250 },
      sort: { field: "last_seen", order: "DESC" },
    },
  );
  const { data: tasks, isPending: tasksPending } = useGetList<Task>("tasks", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "due_date", order: "ASC" },
    filter: { "done_date@is": "null" },
  });

  if (contactsPending || tasksPending) return null;

  const recommendations = buildRecommendations(contacts ?? [], tasks ?? []);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center">
        <div className="mr-3 flex">
          <HeartHandshake className="text-muted-foreground w-6 h-6" />
        </div>
        <h2 className="text-xl font-semibold text-muted-foreground">
          Relationship Action Review
        </h2>
      </div>
      <Card className="py-0">
        <CardHeader className="px-4 py-3 border-b gap-1">
          <CardTitle className="text-sm font-medium">
            Highest-leverage relationship moves
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {recommendations.length ? (
            <ol className="divide-y">
              {recommendations.slice(0, 7).map((recommendation) => (
                <li key={recommendation.contact.id}>
                  <Link
                    to={`/contacts/${recommendation.contact.id}/show`}
                    className="block px-4 py-3 hover:bg-muted/60 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium truncate">
                            {formatName(recommendation.contact)}
                          </span>
                          {recommendation.contact.status ? (
                            <Badge variant="secondary">
                              {recommendation.contact.status === "A"
                                ? "A - Strong"
                                : recommendation.contact.status === "B"
                                  ? "B - Medium"
                                  : "C - Weak"}
                            </Badge>
                          ) : null}
                          {recommendation.humanReviewRequired ? (
                            <Badge variant="outline">
                              <AlertCircle className="w-3 h-3" />
                              Review
                            </Badge>
                          ) : null}
                        </div>
                        <div className="text-sm text-muted-foreground truncate mt-1">
                          {recommendation.contact.title ||
                            recommendation.contact.company_name ||
                            (recommendation.contact.influence === "1"
                              ? "Decision Maker"
                              : recommendation.contact.influence === "2"
                                ? "Influencer"
                                : recommendation.contact.influence === "3"
                                  ? "Neither"
                                  : null) ||
                            "Relationship record"}
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground mt-1 shrink-0" />
                    </div>
                    <div className="mt-2 text-sm font-medium">
                      {recommendation.action}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
                      {recommendation.reasons.join(" · ")}
                    </div>
                  </Link>
                </li>
              ))}
            </ol>
          ) : (
            <div className="px-4 py-5 text-sm text-muted-foreground">
              No relationship actions need review.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const buildRecommendations = (
  contacts: Contact[],
  tasks: Task[],
): Recommendation[] => {
  const now = new Date();
  const tasksByContact = new Map<Contact["id"], Task[]>();
  tasks.forEach((task) => {
    if (task.done_date) return;
    const existing = tasksByContact.get(task.contact_id) ?? [];
    existing.push(task);
    tasksByContact.set(task.contact_id, existing);
  });

  return contacts
    .map((contact) => {
      const reasons: string[] = [];
      let score = 0;
      const strength = contact.status;
      const role = contact.influence;
      const nextMove = contact.next_relationship_move;
      const nextTouchAt = parseDate(contact.next_touch_date);
      const nextTouchIsDue = nextTouchAt ? nextTouchAt <= now : false;
      const daysSinceMeaningfulInteraction = getDaysSince(
        contact.last_meaningful_interaction_at,
        now,
      );
      const openTaskCount = tasksByContact.get(contact.id)?.length ?? 0;

      if (
        strength &&
        warmStrengths.has(strength) &&
        (daysSinceMeaningfulInteraction === null ||
          daysSinceMeaningfulInteraction >= 60)
      ) {
        score += 60;
        reasons.push(
          daysSinceMeaningfulInteraction === null
            ? "warm relationship has no meaningful interaction date"
            : `warm relationship stale for ${daysSinceMeaningfulInteraction} days`,
        );
      }

      if (
        role &&
        strategicRoles.has(role) &&
        (daysSinceMeaningfulInteraction === null ||
          daysSinceMeaningfulInteraction >= 90)
      ) {
        score += 35;
        reasons.push(`strategic role: ${role}`);
      }

      if (nextTouchIsDue) {
        score += 45;
        reasons.push("next touch is due");
      } else if (
        ["Requires Action", "Overdue", "No Plan"].includes(
          contact.next_touch_status ?? "No Plan",
        )
      ) {
        score += 20;
        reasons.push(`touch status: ${contact.next_touch_status ?? "No Plan"}`);
      }

      if (nextMove) {
        score += 20;
        reasons.push(`next move: ${nextMove}`);
      }

      if (contact.relationship_evidence) {
        score += 10;
        reasons.push("has relationship evidence");
      }

      if (openTaskCount) {
        score -= 25;
        reasons.push(`${openTaskCount} open task(s) already`);
      }

      const action =
        nextMove ||
        (strength && warmStrengths.has(strength)
          ? "Review relationship"
          : "Decide next touch");

      return {
        contact,
        score,
        action,
        reasons,
        openTaskCount,
        daysSinceMeaningfulInteraction,
        humanReviewRequired:
          (strength ? warmStrengths.has(strength) : false) ||
          humanReviewMoves.has(action),
      };
    })
    .filter((recommendation) => recommendation.score > 0)
    .sort((a, b) => b.score - a.score);
};

const parseDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getDaysSince = (value: string | undefined, now: Date) => {
  const date = parseDate(value);
  if (!date) return null;
  return Math.max(
    Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)),
    0,
  );
};

const formatName = (contact: Contact) =>
  [contact.first_name, contact.last_name].filter(Boolean).join(" ") ||
  `Contact ${contact.id}`;
